import React, { useEffect, useRef, useState } from 'react';
import { Button, Text, View } from "../Components/Themed"
import { FlashMarketApi } from './api';
import type { FlashMarketFeeProps, LocationProps, MyPlayerProps, PollCampaignProps, PollOptionProps, PollProps, PollSummaryProps } from '../types';
import { ActivityIndicator, FlatList, Image, type ViewStyle } from 'react-native';
import { useColors } from '../constants/useColors';
import FlashMarket from '.';
import { Icons } from '../Components';
import SocketManager from '../Socket';
import moment from 'moment-mini';

type FlashHolderProps = {
    league_id?:string,
    player_id?:string,
    float?:boolean,
    distinct_id?:string,
    restricted_flash_ids?:string[],
    compact?:boolean,
    player?:MyPlayerProps,
    wrap_options?:boolean,
    company_id?:string,
    init_expanded?:boolean,
    contest_type?:string,
    parent_style?:ViewStyle,
    hide_footer?:boolean,
    poll_campaign_id?:string,
    contest_id?:string,
    hide_tabs?:boolean,
    style?:ViewStyle,
    side_type?:string,
    hide_timer?:boolean,
    no_highlight?:boolean,
    location?:LocationProps,
    side_id?:string,
    default_poll_id?:string,
    poll_id?:string,
    onManageFlash?: (poll:PollProps) => void,
    onSelectCompactPoll?:(poll_id:string) => void,
    onRequestLocation:() => void,
    onFocusPosition?: (position:{ x:number, y:number, width:number, height:number }) => void,
    onRequestAuthenticate: () => void
}
const sections = [ 'selector','explainer','flash_market','options','footer' ]
const FlashHolder = ({ float, poll_campaign_id, restricted_flash_ids, wrap_options, company_id, compact, style, default_poll_id, hide_timer, distinct_id, hide_footer, no_highlight, hide_tabs, parent_style, league_id, init_expanded, player_id, player, location, contest_type, contest_id, side_type, side_id, poll_id, onSelectCompactPoll, onRequestAuthenticate, onFocusPosition, onRequestLocation, onManageFlash } : FlashHolderProps) => {
    const Colors = useColors();
    const [ needs_reload, setNeedsReload ] = useState(false);
    const [ socket_campaigns, setSocketCampaigns ] = useState<PollCampaignProps[]>([]);
    const [ socket_poll_options, setSocketPollOptions ] = useState<{
        options:PollOptionProps[],
        summaries:PollSummaryProps[]
    }>({
        options: [],
        summaries: []
    });

    const [ socket_polls, setSocketPolls ] = useState<PollProps[]>([]);
    const [ market_data, setMarketData ] = useState<{
        loading:boolean,
        poll?:PollProps,
        poll_campaign?:PollCampaignProps,
        flash_market_fees:FlashMarketFeeProps[],
        poll_options:PollOptionProps[],
        poll_summaries:PollSummaryProps[]
    }>({
        loading:false,
        poll_options:[],
        poll_summaries:[],
        flash_market_fees:[]
    }); 
    const { loading, poll_options, poll_summaries, poll_campaign, flash_market_fees } = market_data;
    const flashRef = useRef<any>(null);
    const [ flash_data, setFlashData ] = useState<{
        holder_loading:boolean,
        loaded:boolean,
        flash_markets:PollProps[]
    }>({
        holder_loading:false,
        loaded:false,
        flash_markets: []
    });
    const { holder_loading, flash_markets, loaded } = flash_data;
    const [ active_flash_market, setActiveFlashMarket ] = useState<string | undefined>(undefined);
    const flash_market = flash_markets.find(fm => fm.poll_id == active_flash_market);
    let sorted_flashes = flash_markets.sort((a,b) => moment(a.end_datetime).unix() - moment(b.end_datetime).unix() || parseInt(a.poll_id) - parseInt(b.poll_id))
    if(restricted_flash_ids && restricted_flash_ids.length > 0){ sorted_flashes = sorted_flashes.filter(f => restricted_flash_ids.includes(f.poll_id)) }
    
    useEffect(() => {
        loadData();
    },[league_id, contest_type, contest_id, side_type, side_id, poll_id, default_poll_id]);

    useEffect(() => {
        if(!loaded){ return }
        if(compact){ return } //We dont do any polls if compacted
        let expiring_poll = flash_markets.sort((a,b) => moment(a.end_datetime).unix() - moment(b.end_datetime).unix() || parseInt(a.poll_id) - parseInt(b.poll_id))[0]
        if(poll_id){ return }
        let default_poll = flash_markets.find(fm => fm.poll_id == default_poll_id);
        if(default_poll){ handleSelectPoll(default_poll.poll_id); return }//Return out if we have this!
        if(expiring_poll){ handleSelectPoll(expiring_poll.poll_id) }
    },[loaded])

    useEffect(() => {
        if(socket_campaigns.length == 0){ return }
        if(!poll_campaign){ return }
        let socket_campaign = socket_campaigns.find(sc => sc.poll_campaign_id == poll_campaign.poll_campaign_id);
        if(!socket_campaign){ return }
        setMarketData({
            ...market_data,
            poll_campaign: socket_campaign
        })
    },[JSON.stringify(socket_campaigns)]);

    useEffect(() => {
        if(socket_polls.length == 0){ return }
        addToListOfPolls(socket_polls)
        if(!active_flash_market || !flash_market){ return }
        let same_poll = socket_polls.find(p => p.poll_id == active_flash_market);
        if(!same_poll){ return }
        if(same_poll.status == 'active' && flash_market.status == 'pending'){ loadMarket(flash_market.poll_id); return }
        //See if we have that poll!
        setMarketData({ ...market_data, poll: same_poll });
    }, [JSON.stringify(socket_polls)]);

    const addToListOfPolls = (polls:PollProps[]) => {
        let non_flash_polls = polls.filter(p => !p.flash_active && p.poll_id != poll_id);
        let new_polls = flash_markets.filter(p => !non_flash_polls.find(np => np.poll_id == p.poll_id));
        let flash_polls = polls.filter(p => p.flash_active || p.poll_id == poll_id);
        new_polls = new_polls.filter(fm => !flash_polls.find(np => np.poll_id == fm.poll_id)).concat(flash_polls)
        setFlashData({
            ...flash_data,
            flash_markets: new_polls
        });
    }

    useEffect(() => {
        if(!flash_market){ return }
        const { options, summaries } = socket_poll_options;
        let active_options = options.filter(o => o.poll_id == active_flash_market);
        let active_summaries = summaries.filter(o => o.poll_id == active_flash_market);
        let new_options = poll_options.filter(po => !active_options.find(so => so.poll_option_id == po.poll_option_id)).concat(active_options)
        let new_summaries = poll_summaries.filter(po => !active_summaries.find(so => so.poll_option_id == po.poll_option_id)).concat(active_summaries)
        //See if we have that poll!
        setMarketData({ 
            ...market_data, 
            poll_options: new_options,
            poll_summaries: new_summaries
        });
    }, [JSON.stringify(socket_poll_options)])
    
    const handleSelectPoll = async(poll_id:string, options?:{ reload:boolean }) => {
        //If selected - just remove the selected poll
        if(compact && onSelectCompactPoll){ return onSelectCompactPoll(poll_id) }
        let selected = poll_id == active_flash_market ? true : false
        if(selected && !options?.reload){ return setActiveFlashMarket(undefined) }
        loadMarket(poll_id);
        setActiveFlashMarket(poll_id);

        let active_flash_index = sorted_flashes.findIndex(d => d.poll_id === poll_id);
        if(flashRef && active_flash_index > 0){
            setTimeout(() => {
                flashRef.current?.scrollToIndex({ animated: true, index: active_flash_index - 1 });
            }, 250);      
        }

    }
    

    const loadData = async(options?:{ reload:boolean }) => {
        FlashMarketApi.setEnvironment();
        setFlashData({ ...flash_data, holder_loading: true });
        if(poll_id){ 
            //need to grab the poll
            setFlashData({ ...flash_data, loaded:true, holder_loading: false });
            return handleSelectPoll(poll_id, options);
        }

        if(poll_campaign_id){
            let resp = await FlashMarketApi.getPollsByCampaignId(poll_campaign_id);
            let active_polls = resp.filter(p => p.status != 'closed');
            setFlashData({ ...flash_data, loaded:true, holder_loading: false, flash_markets:active_polls })
        }

        let found_polls:PollProps[] = []
        if(company_id){
            let resp = await FlashMarketApi.getActiveMarkets(company_id);
            found_polls = resp.polls
        } else if(league_id || contest_type || contest_id || side_type || side_id){
            found_polls = await FlashMarketApi.getActiveLinkedFlashMarkets(league_id, contest_type, contest_id, side_type, side_id);
        } else {
            found_polls = await FlashMarketApi.getActiveFlashMarkets();

        }
        setFlashData({
            ...flash_data,
            loaded: true,
            holder_loading:false,
            flash_markets:found_polls
        });
    }

    const loadMarket = async(id:string) => {
        setMarketData({ ...market_data, loading: true })
        const pr = await FlashMarketApi.getPollById(id);
        if(!pr){ return }
        const pc = await FlashMarketApi.getPollCampaignById(pr.poll.poll_campaign_id);
        setMarketData({
            ...market_data,
            poll: pr.poll,
            poll_campaign:pc?.poll_campaign,
            poll_options:pr.poll_options,
            flash_market_fees: pc?.flash_market_fees ?? [],
            poll_summaries: pr.poll_summaries,
            loading:false
        });
        setFlashData({
            ...flash_data,
            flash_markets: flash_markets.filter(mk => mk.poll_id != pr.poll.poll_id).concat(pr.poll)
        })
    }

    const renderFlashes = (data:{ item:PollProps, index:number }) => {
        const selected = active_flash_market == data.item.poll_id ? true : false
        const is_upcoming = data.item && data.item.start_datetime && moment().isBefore(moment(data.item.start_datetime)) ? true : false
        return (
            <Button
                float={selected}
                style={{ padding:4, margin:1, borderRadius:0, borderTopRightRadius:8, borderTopLeftRadius:8, borderWidth:2, borderBottomWidth:0, borderColor:selected && !no_highlight ? Colors.text.success : Colors.borders.light, opacity:selected?1:0.5 }}
                onPress={() => handleSelectPoll(data.item.poll_id)}
            >
                {data.item.poll_image ?
                <Image
                    source={{ uri: data.item.poll_image?.url }}
                    style={{ height:45, width:45 }}
                    resizeMode='cover'
                />
                :
                <View transparent style={{ padding:10 }}>
                    <Icons.FlameIcon
                        size={30}
                        color={Colors.text.h1}
                    />
                </View>
                }
                {is_upcoming ?
                <View transparent style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
                    <Icons.LockClosedIcon size={20} color={Colors.text.h1} />
                </View>
                :<></>}
            </Button>
        )
    }

    const renderSections = (data:{item:string, index:number}) => {
        switch(data.item){
            case 'selector':
                if(hide_tabs){ return <></> }
                return (
                    <View transparent style={{ flexDirection:'row' }}>
                        <View transparent style={{ flex:1 }}>
                            <FlatList
                                key={'poll_selector'}
                                ref={flashRef}
                                keyExtractor={(item) => item.poll_id.toString()}
                                data={sorted_flashes}
                                horizontal
                                renderItem={renderFlashes}
                            />
                        </View>
                        <View
                            transparent
                            style={{ justifyContent:'flex-end', alignItems:'center', padding:5, borderLeftWidth:1,  borderColor: Colors.borders.light }}
                        >
                            <Icons.FlameIcon size={20} color={Colors.text.warning} />
                            <Text style={{ marginTop:5 }} theme='warning' textAlign='center'>FLASH</Text>
                            {holder_loading ?
                            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
                                <ActivityIndicator
                                    size='small'
                                    color={Colors.text.h1}
                                />
                            </View>
                            :<></>}
                        </View>
                    </View>
                )
            case 'explainer':
                if(active_flash_market){ return <></> }
                if(hide_tabs){ return <></> }
                return (
                    <View transparent style={{ padding:10, borderTopWidth:1, borderColor:Colors.borders.light }}>
                        <Text theme='h2'>Select a tab above to bet the flash market!</Text>
                    </View>
                )
            case 'flash_market':
                if(!active_flash_market){ return <></> }
                return (
                    <View transparent>
                        <FlashMarket
                            player_id={player_id}
                            player={player}
                            wrap_options={wrap_options}
                            location={location}
                            poll={flash_market}
                            poll_options={poll_options}
                            poll_summaries={poll_summaries}
                            hide_footer={hide_footer}
                            poll_campaign={poll_campaign}
                            flash_market_fees={flash_market_fees}
                            loading={loading || holder_loading}
                            hide_image
                            hide_timer={hide_timer}
                            card_style={{ borderWidth:2, borderColor:!no_highlight?Colors.text.success:Colors.borders.light, borderBottomRightRadius:8, borderBottomLeftRadius:8 }}
                            init_expanded={init_expanded}
                            footer_style={{ borderBottomRightRadius:8, borderBottomLeftRadius:8 }}
                            header_style={{ borderTopRightRadius:0, borderTopLeftRadius:0 }}
                            onReload={() => loadMarket(active_flash_market)}
                            onRequestAuthenticate={onRequestAuthenticate}
                            onManageFlash={onManageFlash}
                            onRequestLocation={onRequestLocation}
                            onFocusPosition={onFocusPosition}
                        />
                    </View>
                )
            default: return <></>
        }
    }
    return (
        <View transparent style={{ ...parent_style }}>
            {flash_markets.length > 0 || active_flash_market ?
            <View float={float} style={{ ...style }}>
                <FlatList
                    key={'flash_sections'}
                    keyExtractor={item => item}
                    renderItem={renderSections}
                    data={sections}
                />
            </View>
            :<></>}
            <SocketManager
                distinct_id={`flash_${distinct_id}`}
                onConnect={() => {
                    if(needs_reload){
                        setNeedsReload(false);
                        loadData({ reload: true });
                    }
                    return
                }}
                onDisconnect={() => setNeedsReload(true)}
                onSocketEvent={(ev) => {
                    switch(ev.type){
                        case 'UPDATE_POLL_CAMPAIGNS':
                            setSocketCampaigns(ev.poll_campaigns)
                            break;
                        case 'UPDATE_POLLS':
                            if(ev.polls.length == 0){ return }
                            if(!ev.polls[0]){ return }
                            setSocketPolls(ev.polls)
                            break
                        case 'UPDATE_POLL_OPTIONS':
                            setSocketPollOptions({
                                options: ev.poll_options,
                                summaries: ev.poll_summaries
                            })
                            break
                        default: break;
                    }
                }}
                subscribed_events={['UPDATE_POLL_CAMPAIGNS', 'UPDATE_POLLS','UPDATE_POLL_OPTIONS']}
            />
        </View>
    )
}

export default FlashHolder